// Pure helpers and API functions for the Agenda tab.
import { AGENDA_STATE } from "./agenda.constants";
import LogManager from "../../../utils/logManager";
import { parseLambdaPayload } from "../../../utils/routerLambdaUtils";

/** Completed/total counts for the tab header badge (e.g. "2/5"). */
export const getAgendaCounts = (items = []) => ({
  completed: items.filter((item) => item.state === AGENDA_STATE.COMPLETED).length,
  total: items.length,
});

// Backend uses itemId/content/status; frontend uses id/title/state.
const STATUS_TO_STATE = {
  completed: AGENDA_STATE.COMPLETED,
  in_progress: AGENDA_STATE.IN_PROGRESS,
  pending: AGENDA_STATE.UPCOMING,
};

/**
 * Build a frontend agenda row. Single source for the row shape, so an optimistic add and a fetched
 * item are structurally identical — fromBackend routes through it, meaning a field added here
 * covers both paths and the two builders cannot drift.
 *
 * `detectedAt` / `amendedAt` are not rendered — they are the row's version for
 * useInsightStore.versionOf. A row carrying neither never enters the version map, so every
 * agenda_status_changed push for it is dropped.
 */
export const makeAgendaItem = ({
  id,
  title,
  state = AGENDA_STATE.UPCOMING,
  recap = null,
  itemStartedAt = null,
  statusUpdatedBy = null,
  amendmentCount = 0,
  detectedAt = null,
  amendedAt = null,
}) => ({
  id,
  title,
  state,
  recap,
  itemStartedAt,
  statusUpdatedBy,
  amendmentCount,
  detectedAt,
  amendedAt,
});

/**
 * Map a backend agenda item to a frontend row. Exported so the shared insight-polling hook can
 * normalize fetch/poll responses (`mapItems`) and the store can normalize WebSocket payloads
 * (`mapAgendaItem`) — both arrive in the backend shape.
 *
 * Safe to use for a whole-row replace because an agenda push carries every field this produces —
 * see the completeness note on InsightPushLambda.buildPayload.
 */
export const fromBackend = (item) => makeAgendaItem({
  id: item.itemId,
  title: item.content,
  state: STATUS_TO_STATE[item.status] || AGENDA_STATE.UPCOMING,
  recap: item.recap || null,
  itemStartedAt: item.itemStartedAt || null,
  statusUpdatedBy: item.statusUpdatedBy || null,
  amendmentCount: item.amendmentCount ?? 0,
  detectedAt: item.detectedAt || null,
  amendedAt: item.amendedAt || null,
});

/**
 * Id of the completed item that most recently finished, or null — used to pick which recap opens
 * on its own.
 *
 * Maxes `itemStartedAt` rather than taking the last completed row in sort order: nulls sort last
 * there, so one completed item without a start time would park at the tail and win.
 *
 * Parsed to epoch ms rather than compared as strings — Instant.toString() omits zero fractional
 * seconds, and "...00.500Z" sorts before "...00Z" lexically because "." precedes "Z".
 */
export const mostRecentlyCompletedId = (items) => {
  let newestId = null;
  let newestMs = null;
  for (const item of items || []) {
    if (item.state !== AGENDA_STATE.COMPLETED || !item.recap || !item.itemStartedAt) {
      continue;
    }
    const ms = Date.parse(item.itemStartedAt);
    if (Number.isNaN(ms)) {
      continue;
    }
    if (newestMs == null || ms > newestMs) {
      newestMs = ms;
      newestId = item.id;
    }
  }
  return newestId;
};

/** Rank for the primary sort key: completed items first, then in progress, then upcoming. */
const STATE_RANK = {
  [AGENDA_STATE.COMPLETED]: 0,
  [AGENDA_STATE.IN_PROGRESS]: 1,
  [AGENDA_STATE.UPCOMING]: 2,
};

/** Numeric N from a prepopulated "agenda#N" id; null for user-added ids (which carry a UUID). */
const prepopulatedIndex = (id) => {
  const suffix = typeof id === "string" && id.startsWith("agenda#") ? id.slice("agenda#".length) : null;
  return suffix != null && /^\d+$/.test(suffix) ? Number(suffix) : null;
};

/**
 * Nulls and unparseable values sort last; otherwise chronological.
 *
 * Parsed rather than compared as strings: Instant.toString() omits zero fractional seconds, so
 * "...00.500Z" would sort before "...00Z" lexically because "." precedes "Z".
 */
const byNullableAscending = (a, b) => {
  const aMs = a == null ? NaN : Date.parse(a);
  const bMs = b == null ? NaN : Date.parse(b);
  const aMissing = Number.isNaN(aMs);
  const bMissing = Number.isNaN(bMs);
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return aMs - bMs;
};

/**
 * Orders the agenda for display: completed, then in progress, then upcoming.
 *
 * Within the discussed states, by when discussion started. Within upcoming, invite-derived items
 * (in their original agenda#N order) before user-added ones (oldest first).
 *
 * Applied client-side on every store write rather than taking the order /agenda returns, because
 * status arrives over the WebSocket and pushes merge in place — a server-supplied order would only
 * refresh on a fetch, and the poll that would carry it runs solely while the socket is down.
 */
export const compareAgendaItems = (a, b) => {
  const rankDiff = (STATE_RANK[a.state] ?? STATE_RANK[AGENDA_STATE.UPCOMING])
    - (STATE_RANK[b.state] ?? STATE_RANK[AGENDA_STATE.UPCOMING]);
  if (rankDiff !== 0) return rankDiff;

  if (a.state !== AGENDA_STATE.UPCOMING) {
    const startedDiff = byNullableAscending(a.itemStartedAt, b.itemStartedAt);
    if (startedDiff !== 0) return startedDiff;
  }

  const ia = prepopulatedIndex(a.id);
  const ib = prepopulatedIndex(b.id);
  if (ia != null && ib != null) return ia - ib;
  if (ia != null) return -1;
  if (ib != null) return 1;

  return byNullableAscending(a.detectedAt, b.detectedAt);
};

/** POST to /agenda with the given body. */
const agendaFetch = async (body) => {
  const response = await fetch("/agenda", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseLambdaPayload(response, "/agenda");
};

/** Fetch all agenda items for a meeting. */
export const fetchAgendaItems = async (meetingUuid) => {
  LogManager.info(`Fetching agenda items for meeting ${meetingUuid}`);
  const data = await agendaFetch({ meetingUuid, operation: "query" });
  return (data.agendaItems || []).map(fromBackend);
};

/** Add an agenda item. Returns the frontend-shaped item on success. */
export const addAgendaItem = async (meetingUuid, itemId, content) => {
  LogManager.info(`Adding agenda item ${itemId} for meeting ${meetingUuid}`);
  const data = await agendaFetch({ meetingUuid, operation: "add", itemId, content });
  if (data.error === "CONFLICT") {
    LogManager.warn(`Agenda item ${itemId} already exists (duplicate add)`);
    return null;
  }
  return fromBackend(data.agendaItem);
};

/**
 * Update an agenda item's content.
 *
 * Deliberately does NOT send `expectedVersion`, even though `/agenda` now returns `amendmentCount`
 * (the optimistic-lock version) so the client could. Concurrent-edit races are out of scope for
 * beta: omitting it makes the backend fall back to an existence check, i.e. last-write-wins.
 *
 * To enable optimistic locking: pass the row's `amendmentCount` as `expectedVersion` and surface
 * the CONFLICT to the user instead of silently reverting.
 */
export const updateAgendaItem = async (meetingUuid, itemId, content) => {
  LogManager.info(`Updating agenda item ${itemId} for meeting ${meetingUuid}`);
  const data = await agendaFetch({ meetingUuid, operation: "update", itemId, content });
  if (data.error === "CONFLICT") {
    LogManager.warn(`Agenda item ${itemId} version conflict`);
    return null;
  }
  return fromBackend(data.agendaItem);
};

/** Delete an agenda item. */
export const deleteAgendaItem = async (meetingUuid, itemId) => {
  LogManager.info(`Deleting agenda item ${itemId} from meeting ${meetingUuid}`);
  await agendaFetch({ meetingUuid, operation: "delete", itemId });
};
